/**
 * Distance calculation utilities using the Haversine formula
 * Calculates straight-line distance between two geographical points
 */

/**
 * Calculate the straight-line distance between two points on Earth
 * using the Haversine formula
 * 
 * @param lat1 - Latitude of first point (degrees)
 * @param lng1 - Longitude of first point (degrees)
 * @param lat2 - Latitude of second point (degrees)
 * @param lng2 - Longitude of second point (degrees)
 * @returns Distance in miles
 */
export function calculateDistance(
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number {
  // Validate inputs
  if (!isValidCoordinate(lat1, lng1) || !isValidCoordinate(lat2, lng2)) {
    console.warn('Invalid coordinates provided to calculateDistance');
    return 0;
  }

  const R = 3959; // Earth's radius in miles (use 6371 for kilometers)
  
  // Convert degrees to radians
  const dLat = toRadians(lat2 - lat1);
  const dLng = toRadians(lng2 - lng1);
  
  // Haversine formula
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
  const distance = R * c; // Distance in miles
  
  return distance;
}

/**
 * Convert degrees to radians
 */
function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
} 

/**
 * Validate that coordinates are within valid ranges
 * Latitude: -90 to 90
 * Longitude: -180 to 180
 */
function isValidCoordinate(lat: number, lng: number): boolean {
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    !isNaN(lat) &&
    !isNaN(lng) &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180
  );
}

/**
 * Format distance for display
 * @param miles - Distance in miles
 * @param decimals - Number of decimal places (default: 1)
 * @returns Formatted string like "2.3 mi"
 */
export function formatDistance(miles: number, decimals: number = 1): string {
  if (miles < 0.1) {
    return '< 0.1 mi';
  }
  
  return `${miles.toFixed(decimals)} mi`;
}

/**
 * Calculate distances for multiple camps from a single user location
 * @param userLat - User latitude
 * @param userLng - User longitude
 * @param camps - Array of camps with latitude/longitude
 * @returns Array of camps with distance_miles added
 */
export function calculateDistances<T extends { latitude: number; longitude: number }>(
  userLat: number,
  userLng: number,
  camps: T[]
): (T & { distance_miles: number })[] {
  return camps.map(camp => ({
    ...camp,
    distance_miles: calculateDistance(userLat, userLng, camp.latitude, camp.longitude)
  }));
}

/**
 * Sort camps by proximity (closest first)
 * @param camps - Array of camps with distance_miles
 * @returns Sorted array (mutates original)
 */
export function sortByProximity<T extends { distance_miles: number }>(camps: T[]): T[] {
  return camps.sort((a, b) => a.distance_miles - b.distance_miles);
}
